<script>
export default {
  name: "ReusableColorPicker",
  props: {
    value: {
      type: [String, Object],
      default: "#1976D2",
    },
    showSwatches: {
      type: Boolean,
      default: true,
    },
    customSwatches: {
      type: Array,
      default: undefined, // Corrected: Use undefined instead of null
    },
    showLabel: {
      type: Boolean,
      default: true,
    },
    labelText: {
      type: String,
      default: "Color",
    },
    showDetails: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    hideCanvas: {
      type: Boolean,
      default: false,
    },
    hideInputs: {
      type: Boolean,
      default: false,
    },
    hideSliders: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: "rgba",
    },
    width: {
      type: [String, Number],
      default: 300,
    },
  },
  // ... rest of your script remains the same
  data() {
    return {
      menu: false,
      pickerMode: this.mode,
    };
  },
  computed: {
    swatchStyle() {
      const { menu } = this;
      return {
        backgroundColor: this.value ?? "#000000",
        cursor: "pointer",
        height: "30px",
        width: "30px",
        borderRadius: menu ? "4px" : "4px",
        border: '1px solid black',
        transition: "border-radius 200ms ease-in-out",
        // marginTop: "17px",
      };
    },
    internalColorValue: {
      get() {
        return this.value;
      },
      set() {
        // Setter for v-color-picker's internal v-model.
        // The 'input' event is emitted in the updateColor method.
      },
    },
    colorObject() {
      if (typeof this.value === "string") {
        if (this.value.startsWith("#")) {
          const hex = this.value;
          let r = 0,
            g = 0,
            b = 0,
            a = 1;
          if (hex.length === 4) {
            // #RGB
            r = parseInt(hex[1] + hex[1], 16);
            g = parseInt(hex[2] + hex[2], 16);
            b = parseInt(hex[3] + hex[3], 16);
          } else if (hex.length === 7) {
            // #RRGGBB
            r = parseInt(hex.substring(1, 3), 16);
            g = parseInt(hex.substring(3, 5), 16);
            b = parseInt(hex.substring(5, 7), 16);
          } else if (hex.length === 9) {
            // #RRGGBBAA
            r = parseInt(hex.substring(1, 3), 16);
            g = parseInt(hex.substring(3, 5), 16);
            b = parseInt(hex.substring(5, 7), 16);
            a = parseInt(hex.substring(7, 9), 16) / 255;
          }
          return {
            hex: this.value,
            hexa: this.value,
            rgba: { r, g, b, a },
            hsla: this.hexToHsla(this.value),
          };
        }
        return {
          hex: this.value,
          hexa: this.value,
          rgba: { r: 0, g: 0, b: 0, a: 1 },
          hsla: { h: 0, s: 0, l: 0, a: 1 },
        }; // Fallback with default rgba/hsla
      }
      return (
        this.value || {
          hex: "#FFFFFF",
          hexa: "#FFFFFFFF",
          rgba: { r: 255, g: 255, b: 255, a: 1 },
          hsla: { h: 0, s: 0, l: 1, a: 1 },
        }
      ); // Ensure it's an object
    },
    displayHexColor() {
      return (
        this.colorObject.hex ||
        (typeof this.value === "string" ? this.value : "") ||
        "#FFFFFF"
      );
    },
    displayRgbaColor() {
      const rgba = this.colorObject.rgba;
      return rgba
        ? `rgba(${rgba.r}, ${rgba.g}, ${rgba.b}, ${parseFloat(
            rgba.a.toFixed(2)
          )})`
        : null;
    },
    displayHslaColor() {
      const hsla = this.colorObject.hsla;
      return hsla
        ? `hsla(${Math.round(hsla.h)}, ${Math.round(
            hsla.s * 100
          )}%, ${Math.round(hsla.l * 100)}%, ${parseFloat(hsla.a.toFixed(2))})`
        : null;
    },
  },
  watch: {
    mode(newMode) {
      this.pickerMode = newMode;
    },
    pickerMode(newPickerMode) {
      this.$emit("update:mode", newPickerMode);
    },
    value() {
      // This watcher helps keep internal state consistent if the prop changes from parent
      // The :value binding on v-color-picker should handle the update automatically
    },
  },
  methods: {
    updateColor(newColor) {
      // Convert any input to hex before emitting
      const hexValue = this.convertToHex(newColor);
      this.$emit("input", hexValue);
      this.$emit("change", hexValue);
    },
    convertToHex(color) {
      if (typeof color === 'string' && color.startsWith('#')) {
        return color;
      }
      if (typeof color === 'object' && color !== null) {
        if (color.r !== undefined && color.g !== undefined && color.b !== undefined) {
          const r = Math.round(color.r).toString(16).padStart(2, '0');
          const g = Math.round(color.g).toString(16).padStart(2, '0');
          const b = Math.round(color.b).toString(16).padStart(2, '0');
          const a = color.a !== undefined ? Math.round(color.a * 255).toString(16).padStart(2, '0') : 'ff';
          return `#${r}${g}${b}${a.length === 2 ? a : ''}`;
        } else if (color.hex) {
          return color.hex;
        }
      }
      console.warn("Could not convert color to hex:", color);
      return this.colorObject.hex || "#000000"; // Fallback to current hex or black
    },
    textColorForBg(bgColor) {
      if (!bgColor || typeof bgColor !== "string" || !bgColor.startsWith("#")) {
        return "black";
      }
      const color =
        bgColor.charAt(0) === "#" ? bgColor.substring(1, 7) : bgColor;
      const r = parseInt(color.substring(0, 2), 16);
      const g = parseInt(color.substring(2, 4), 16);
      const b = parseInt(color.substring(4, 6), 16);
      const uicolors = [r / 255, g / 255, b / 255];
      const c = uicolors.map((col) => {
        if (col <= 0.03928) {
          return col / 12.92;
        }
        return Math.pow((col + 0.055) / 1.055, 2.4);
      });
      const L = 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
      return L > 0.179 ? "black" : "white";
    },
    hexToHsla(hex) {
      if (!hex || typeof hex !== "string" || !hex.startsWith("#"))
        return { h: 0, s: 0, l: 0, a: 1 };

      let r = 0,
        g = 0,
        b = 0,
        a = 1;
      if (hex.length === 4) {
        // #RGB
        r = parseInt(hex[1] + hex[1], 16);
        g = parseInt(hex[2] + hex[2], 16);
        b = parseInt(hex[3] + hex[3], 16);
      } else if (hex.length === 7) {
        // #RRGGBB
        r = parseInt(hex.substring(1, 3), 16);
        g = parseInt(hex.substring(3, 5), 16);
        b = parseInt(hex.substring(5, 7), 16);
      } else if (hex.length === 9) {
        // #RRGGBBAA
        r = parseInt(hex.substring(1, 3), 16);
        g = parseInt(hex.substring(3, 5), 16);
        b = parseInt(hex.substring(5, 7), 16);
        a = parseInt(hex.substring(7, 9), 16) / 255;
      } else {
        // Invalid hex
        return { h: 0, s: 0, l: 0, a: 1 };
      }

      r /= 255;
      g /= 255;
      b /= 255;
      const max = Math.max(r, g, b),
        min = Math.min(r, g, b);
      let h,
        s,
        l = (max + min) / 2;

      if (max === min) {
        h = s = 0; // achromatic
      } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
          case r:
            h = (g - b) / d + (g < b ? 6 : 0);
            break;
          case g:
            h = (b - r) / d + 2;
            break;
          case b:
            h = (r - g) / d + 4;
            break;
          default:
            h = 0; // Should not happen
        }
        h /= 6;
      }
      return { h: h * 360, s, l, a };
    },
  },
};
</script>

<template>
  <v-menu
    v-model="menu"
    nudge-bottom="105"
    nudge-left="16"
    :close-on-content-click="false"
    hide-details
  >
    <template v-slot:activator="{ on }">
      <div
        style="display: flex; flex-direction: column; align-items: flex-start"
      >
        <span style="font-size: 0.7em; margin-bottom: 2px">{{
          labelText
        }}</span>
        <div :style="swatchStyle" v-on="on" />
      </div>
    </template>
    <v-card outlined class="reusable-color-picker-card">
      <v-card-text>
        <v-row align="center">
          <v-col :cols="showLabel ? 8 : 12" :md="showLabel ? 7 : 12">
            <v-color-picker
              :value="internalColorValue"
              @input="updateColor"
              :show-swatches="showSwatches"
              :swatches="customSwatches"
              :disabled="disabled"
              :hide-canvas="hideCanvas"
              :hide-inputs="hideInputs"
              :hide-sliders="hideSliders"
              :mode.sync="pickerMode"
              :width="width"
              class="mx-auto"
            ></v-color-picker>
          </v-col>

          <v-col v-if="showLabel" cols="4" md="5">
            <div class="selected-color-info">
              <h4 v-if="labelText">{{ labelText }}</h4>
              <template v-if="internalColorValue">
                <v-chip
                  :color="displayHexColor"
                  :text-color="textColorForBg(displayHexColor)"
                  class="mb-2 color-chip"
                  small
                >
                  {{ displayHexColor || "N/A" }}
                </v-chip>

                <div
                  class="color-preview"
                  :style="{
                    backgroundColor: displayHexColor,
                    border: '1px solid black',
                  }"
                ></div>

                <div v-if="showDetails" class="color-details mt-2">
                  <p v-if="displayRgbaColor">
                    <strong>RGBA:</strong> {{ displayRgbaColor }}
                  </p>
                  <p v-if="displayHslaColor">
                    <strong>HSLA:</strong> {{ displayHslaColor }}
                  </p>
                </div>
              </template>
              <template v-else>
                <p>No color selected.</p>
              </template>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-menu>
</template>

<style scoped>
.reusable-color-picker-card {
  /* Add any specific styling for the card itself if needed */
}

.selected-color-info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding-left: 16px; /* Adjust as needed */
}

.selected-color-info h4 {
  margin-bottom: 8px;
}

.color-chip {
  font-weight: bold;
}

.color-preview {
  width: 100%;
  max-width: 120px; /* Max width for the preview box */
  height: 60px; /* Height for the preview box */
  border-radius: 4px;
  margin-bottom: 8px;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.1); /* Subtle border for light colors */
}

.color-details p {
  margin-bottom: 2px;
  font-size: 0.875rem; /* Smaller font for details */
  word-break: break-all;
}
</style>